import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useNavigate } from "react-router";

const Stepper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding: 0 40px 0 0;
`;

const Container = styled.div`
  width: 100%;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  padding: 30px 100px 30px 40px;
  position: sticky;
`;

const Step = styled.div<{ $active?: boolean; $clickable?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  padding: 6px 16px 6px 6px;
  border-radius: 20px;
  background-color: ${({ $active }) =>
    $active ? "var(--sumi-dark-blue)" : "transparent"};
  transition: background-color 0.2s ease;

  &:hover {
    ${({ $clickable, $active }) =>
      $clickable &&
      !$active &&
      `
      span {
        color: var(--sumi-dark-blue);
        font-weight: 600 !important;
      }
    `}
  }
`;

const Circle = styled.div<{ $active?: boolean; $clickable?: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${({ $active }) =>
    $active ? "white" : "var(--sumi-light-blue)"};
  color: ${({ $active }) => ($active ? "var(--sumi-dark-blue)" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  ${({ $clickable, $active }) =>
    $clickable &&
    !$active &&
    `
    &:hover {
      background-color: var(--sumi-dark-blue);
      opacity: 0.8;
    }
  `}
`;

const Line = styled.div`
  height: 2px;
  background-color: var(--sumi-light-blue);
  flex-grow: 1;
  margin: auto 20px;
`;

interface ReportStepperProps {
  activeStep?: number;
  steps: string[];
  onStepClick?: (stepIndex: number) => void;
  disableNavigation?: boolean;
}

const ReportStepper: React.FC<ReportStepperProps> = ({
  activeStep,
  steps,
  onStepClick,
  disableNavigation = false,
}: ReportStepperProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleStepClick = (index: number) => {
    if (!disableNavigation && onStepClick) {
      onStepClick(index);
    }
  };

  return (
    <Stepper>
      <Container>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <Step
              $active={activeStep === index}
              $clickable={!disableNavigation && !!onStepClick && index !== 3}
              onClick={() => handleStepClick(index)}
            >
              <Circle
                $active={activeStep === index}
                $clickable={!disableNavigation && !!onStepClick && index !== 3}
              >
                {index + 1}
              </Circle>
              <span
                style={{
                  fontWeight: activeStep === index ? "600" : "400",
                  color: activeStep === index ? "white" : undefined,
                }}
              >
                {step}
              </span>
            </Step>
            {index < steps.length - 1 && <Line />}
          </React.Fragment>
        ))}
      </Container>
    </Stepper>
  );
};

export default ReportStepper;
